import styles from './EvieBarList.module.css';

export interface EvieBarListProps {
  /** Optional unit hint (e.g. "USD"); reserved for future reuse (energy, etc.). */
  unit?: string;
  /** Pre-sorted rows; bar widths are scaled to the largest value. */
  bars: Array<{ label: string; value: number; display?: string }>;
}

/**
 * Small presentational bar list for the admin EVII chat — a label, a
 * proportional bar, and a value per row. The one net-new visual primitive (the
 * dashboard has no reusable categorical-bar component); theme-styled with the
 * admin chat's CSS tokens so it sits on-brand beside the bubble + dashboard.
 *
 * Pure presentation: it takes its data via props (from a scoped tool's visual
 * spec) and renders — no data access of its own, so the boundary is untouched.
 */
export function EvieBarList({ bars }: EvieBarListProps) {
  const max = bars.reduce((m, b) => Math.max(m, b.value), 0) || 1;
  return (
    <div className={styles.list}>
      {bars.map((b, i) => {
        // Floor at 2% so a non-zero-but-tiny site still shows a sliver of bar.
        const pct = b.value > 0 ? Math.max(2, Math.round((b.value / max) * 100)) : 0;
        return (
          <div key={`${b.label}-${i}`} className={styles.row}>
            <span className={styles.label} title={b.label}>{b.label}</span>
            <span className={styles.track}>
              <span className={styles.fill} style={{ width: `${pct}%` }} />
            </span>
            <span className={styles.value}>{b.display ?? b.value.toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  );
}
